import React, { useState, useEffect } from 'react';
import { recordStreakPlay } from '../../lib/streak';
import { trackScoreShared, trackLeaderboardSubmitted } from '../../lib/analytics';

// ── Share helpers ─────────────────────────────────────────────────────────────
function buildShareText(game, puzzle) {
  const modeLabel = game.suggestionsOff ? '🚫 No suggestions' : '💬 Suggestions on';
  const lines = [
    `⚽ Hat Trick Grid #${puzzle.puzzle_number || ''}`.trim(),
    `${game.correctCount}/9 cells · ${game.guessesUsed}/${game.maxGuesses} guesses · Score: ${game.score.toLocaleString()}`,
    modeLabel,
    '',
    ...[0, 1, 2].map(r => [0, 1, 2].map(c => game.grid[r][c]?.correct ? '🟢' : '🔴').join('')),
    '',
    'hattrickgrid.com',
  ];
  return lines.join('\n');
}

function ShareMenu({ text, onClose, onTrack }) {
  const encoded = encodeURIComponent(text);

  const options = [
    {
      label: 'Twitter / X',
      icon: '𝕏',
      action: () => { window.open(`https://twitter.com/intent/tweet?text=${encoded}`, '_blank'); onTrack('twitter'); },
    },
    {
      label: 'WhatsApp',
      icon: '💬',
      action: () => { window.open(`https://wa.me/?text=${encoded}`, '_blank'); onTrack('whatsapp'); },
    },
    {
      label: 'Copy to clipboard',
      icon: '📋',
      action: async (setLabel) => {
        await navigator.clipboard.writeText(text);
        onTrack('clipboard');
        setLabel('✓ Copied!');
        setTimeout(onClose, 1000);
      },
    },
  ];

  return (
    <div className="absolute bottom-full mb-2 left-0 right-0 bg-surface border border-borderB rounded-xl overflow-hidden shadow-xl z-10"
         onClick={e => e.stopPropagation()}>
      {options.map(opt => (
        <ShareOption key={opt.label} opt={opt} onClose={onClose} />
      ))}
    </div>
  );
}

function ShareOption({ opt, onClose }) {
  const [label, setLabel] = useState(opt.label);
  return (
    <button
      onClick={() => opt.action(setLabel)}
      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-dimText hover:bg-surface2 hover:text-white transition-colors border-b border-border last:border-0"
    >
      <span className="text-base w-5 text-center">{opt.icon}</span>
      {label}
    </button>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────
export default function ResultModal({ game, puzzle, onClose }) {
  const [username,     setUsername]     = useState('');
  // Use game.leaderboardSubmitted as source of truth — it's reactive React state
  const submitted = game.leaderboardSubmitted;
  const [shareOpen,    setShareOpen]    = useState(false);
  const [revealed,     setRevealed]     = useState(false);

  const won              = game.gameResult?.outcome === 'won';
  const mult             = game.gameResult?.multiplier || 1.0;
  const guessesRemaining = game.maxGuesses - game.guessesUsed;
  const modeLabel        = game.suggestionsOff ? '🚫 No suggestions mode' : '💬 Suggestions on';
  const shareText        = buildShareText(game, puzzle);

  // Record streak on completion (won or lost — showing up counts)
  const [streak, setStreak] = useState(null);
  useEffect(() => {
    if (puzzle?.publish_date) {
      const result = recordStreakPlay(puzzle.publish_date);
      setStreak(result);
    }
  }, [puzzle?.publish_date]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Close share menu when clicking outside
  useEffect(() => {
    if (!shareOpen) return;
    const handler = () => setShareOpen(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [shareOpen]);

  // Use native share sheet on mobile if available
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
        trackScoreShared({ method: 'native', puzzleNumber: puzzle.puzzle_number, score: game.score });
        return;
      } catch (e) {
        // User cancelled or not supported — fall through to menu
      }
    }
    setShareOpen(v => !v);
  };

  const handleSubmitScore = async () => {
    if (!username.trim()) return;
    await game.submitToLeaderboard(username.trim());
    trackLeaderboardSubmitted({ puzzleNumber: puzzle.puzzle_number, score: game.score, correctCount: game.correctCount });
  };

  return (
    // Backdrop — click outside to close
    <div
      className="fixed inset-0 bg-black/85 backdrop-blur-lg flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-borderB rounded-2xl p-7 max-w-sm w-full shadow-[0_40px_80px_rgba(0,0,0,0.6)] relative"
        style={{ animation: 'popIn 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full border border-border text-dimText hover:border-accent hover:text-white transition-colors text-xs"
        >✕</button>

        {/* Title */}
        <h2 className={`font-display text-4xl tracking-[3px] text-center mb-1 ${won ? 'text-correct' : 'text-wrong'}`}>
          {won ? 'FULL TIME!' : 'FINAL WHISTLE'}
        </h2>
        <p className="text-center text-sm text-dimText mb-1">
          {won
            ? `All 9 cells! ${guessesRemaining} guess${guessesRemaining !== 1 ? 'es' : ''} to spare${mult > 1 ? ` · ×${mult.toFixed(2)} efficiency` : ''}`
            : `You got ${game.correctCount}/9. Better luck tomorrow.`}
        </p>
        <p className="text-center text-xs font-mono text-dimText mb-5">{modeLabel}</p>

        {/* Result grid */}
        <div className="grid grid-cols-3 gap-1.5 mb-5">
          {[0, 1, 2].map(r => [0, 1, 2].map(c => {
            const isCorrect   = game.grid[r][c]?.correct;
            const playerName  = game.grid[r][c]?.answer;
            const cell        = puzzle.puzzle_cells?.find(pc => pc.row_index === r && pc.col_index === c);
            const revealName  = !isCorrect && revealed ? (cell?.valid_answers?.[0] || null) : null;
            return (
              <div
                key={`${r}-${c}`}
                className={`rounded-lg flex flex-col items-center justify-center text-center p-1.5
                  ${isCorrect
                    ? 'bg-correct/15 border border-correct/30 h-12'
                    : revealName
                    ? 'bg-accent3/10 border border-accent3/30 min-h-[48px]'
                    : 'bg-wrong/15 border border-wrong/30 h-12'}`}
              >
                {isCorrect ? (
                  <>
                    <div className="text-sm">✅</div>
                    {playerName && (
                      <div className="text-[9px] text-correct/70 leading-tight mt-0.5 px-0.5">
                        {playerName.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
                      </div>
                    )}
                  </>
                ) : revealName ? (
                  <>
                    <div className="text-[9px] font-mono text-accent3/60 uppercase tracking-widest mb-0.5">answer</div>
                    <div className="text-[10px] font-semibold text-accent3 leading-tight px-0.5">
                      {revealName.split(' ').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
                    </div>
                  </>
                ) : (
                  <div className="text-xl">❌</div>
                )}
              </div>
            );
          }))}
        </div>

        {/* Streak */}
        {streak && streak.current > 1 && (
          <div className="flex items-center justify-center gap-3 bg-surface2 border border-accent/20 rounded-xl px-4 py-2.5 mb-3">
            <span className="text-xl">{streak.current >= 7 ? '🌟' : streak.current >= 3 ? '🔥' : '⚽'}</span>
            <div className="text-center">
              <div className="text-sm font-semibold text-white">{streak.current}-day streak!</div>
              {streak.best > streak.current && (
                <div className="text-[10px] text-dimText font-mono">Best: {streak.best} days</div>
              )}
              {streak.best === streak.current && streak.current > 1 && (
                <div className="text-[10px] text-accent font-mono">New best! 🎉</div>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex justify-around bg-surface2 border border-border rounded-xl p-4 mb-5">
          <div className="text-center">
            <div className="font-display text-3xl text-correct">{game.correctCount}/9</div>
            <div className="font-mono text-[10px] text-dimText uppercase tracking-widest">Correct</div>
          </div>
          <div className="text-center">
            <div className="font-display text-3xl text-accent2">{game.guessesUsed}/{game.maxGuesses}</div>
            <div className="font-mono text-[10px] text-dimText uppercase tracking-widest">Guesses</div>
          </div>
          <div className="text-center">
            <div className="font-display text-3xl text-accent">{game.score.toLocaleString()}</div>
            <div className="font-mono text-[10px] text-dimText uppercase tracking-widest">Score</div>
          </div>
        </div>

        {/* Submit to leaderboard */}
        {!submitted ? (
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmitScore()}
              placeholder="Your name for leaderboard…"
              maxLength={20}
              className="flex-1 bg-surface2 border border-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-dimText outline-none focus:border-accent transition-colors"
            />
            <button
              onClick={handleSubmitScore}
              disabled={!username.trim()}
              className="bg-accent text-black font-bold text-sm px-4 py-2 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-default"
            >Save</button>
          </div>
        ) : (
          <div className="text-center mb-3">
            <p className="text-sm text-correct">✓ Score saved to leaderboard!</p>
            {game.leaderboardSubmitted && (
              <p className="text-xs text-dimText mt-1">You already submitted a score for this puzzle.</p>
            )}
          </div>
        )}

        {/* Share button with menu */}
        <div className="relative mb-2.5">
          <button
            onClick={handleShare}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-accent to-accent4 text-black font-display text-xl tracking-[2px] transition-opacity hover:opacity-90"
          >
            📤 &nbsp;Share Result
          </button>
          {shareOpen && (
            <ShareMenu
              text={shareText}
              onClose={() => setShareOpen(false)}
              onTrack={(method) => trackScoreShared({ method, puzzleNumber: puzzle.puzzle_number, score: game.score })}
            />
          )}
        </div>

        {/* Close / Reveal */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-transparent border border-borderB text-dimText text-sm font-medium transition-all hover:border-accent hover:text-white"
          >Close</button>
          {!won && !revealed && (
            <button
              onClick={() => setRevealed(true)}
              className="flex-1 py-3 rounded-xl bg-transparent border border-borderB text-dimText text-sm font-medium transition-all hover:border-accent2 hover:text-white"
            >Reveal answers</button>
          )}
        </div>
      </div>
    </div>
  );
}
